import classNames from 'classnames';
import { useIntl } from 'react-intl';
import camelCase from '../../../utils/camelCase';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import InformationBoxAlt from '../../atoms/InformationBoxAlt';
import { showDefaultError } from '../Notifications';
import DutiesAndTaxes from './DutiesAndTaxes';
import PriceSection from './PriceSection';
import PromotionSection from './PromotionSection';
import getInfoBoxTexts from './getInfoBoxTexts';
import './style.scss';

type PriceSummaryProps = {
  total: number;
  shippingCost: number;
  className?: string;
};

const PriceSummary = ({ total, shippingCost, className }: PriceSummaryProps) => {
  const { checkoutState, removeVoucher, removeGiftCard, isLoading, setLoading } = useCheckoutState();
  const { vouchers, payment, shipping, memberships, basket, promotionDiscounts, promotionDiscountTotalAbsolute } =
    checkoutState.orderState;
  const { pricingEstimations, serviceCosts, tax, discount, total: basketTotal } = basket;
  const {
    integrations: { promotion }
  } = checkoutState;

  const { formatMessage } = useIntl();

  const giftCard = payment?.giftCards?.[0];
  const isDutiesAndTaxesEnabled = config.features.dutiesAndTaxes.enabled;
  const { showNetPrices } = config.basket;
  const arePromotionsEnabled = promotion?.codes?.enabled;
  const showSystemPromotion = !!discount.promotion && !!basketTotal.costWithTaxWithoutPromotion;
  const showDiscountsPromotion =
    !!arePromotionsEnabled && !!promotionDiscountTotalAbsolute && !!promotionDiscounts?.length;
  const isPromoCollapsible = showDiscountsPromotion && config.basket.showCollapsiblePromotion;

  const paymentServices = serviceCosts?.filter(e => e.key.includes('payment'));
  const shippingNetValue = serviceCosts?.filter(e => e.key === 'base_shipping_service_cost')[0]?.netValue ?? 0;
  const shippingPrice = showNetPrices ? shippingNetValue : shippingCost;

  const onRemoveGiftCard = async () => {
    try {
      if (giftCard) {
        setLoading(LOADING_TYPES.giftcardRemove(giftCard.recurringPaymentOptionId));
        await removeGiftCard({ id: giftCard.recurringPaymentOptionId });
      }
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'remove_gift_card');
    }
    setLoading(LOADING_TYPES.off);
  };

  const onRemoveVoucher = async (id: string) => {
    setLoading(LOADING_TYPES.voucherRemove(id));
    try {
      await removeVoucher({ identifier: id });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'remove_voucher');
    }
    setLoading(LOADING_TYPES.off);
  };

  const infoBoxTexts = getInfoBoxTexts({
    shippingCostInfo: {
      isShippingCostHintEnabled: config.features.shippingCostToolTip.enabled,
      hasFreeShipping: checkoutState.customer?.flags?.hasFreeShipping
    },
    returnCostInfo: {
      minGrossOrderValueAfterReturns: shipping?.selected?.[0].returnCosts?.minBruttoOrderValueAfterReturns,
      returnServiceCost: shipping?.selected?.[0].returnCosts?.returnServiceCost,
      hasReturnCosts: checkoutState.customer?.data?.customData?.hasReturnCosts
    }
  });

  // This is an emergency fix because COBA subtotal is broken and there is a campaign for a tenant tomorrow
  const availablePackages = checkoutState.orderState.basket.packages.filter(_package => _package.isAvailable);
  const pointsRedeemed =
    memberships?.activated?.[0].redeemedPoints && memberships?.activated?.[0].redeemedPointsInCurrency;

  const subtotal =
    availablePackages
      .flatMap(_package =>
        _package.items.flatMap(item =>
          showNetPrices ? item.total.costWithoutTaxWithDiscount : item.total.costWithTaxWithDiscount
        )
      )
      .reduce((partialSum, a) => partialSum + a, 0) -
    (checkoutState.orderState.vouchers
      ?.filter(voucher => voucher.type === 'absolute')
      .map(voucher => voucher.discount)
      .reduce((partialSum, a) => partialSum + a, 0) ?? 0);

  return (
    <div className={classNames('price-summary', className)}>
      {!arePromotionsEnabled &&
        vouchers?.map(({ id, ...voucher }) => (
          <PriceSection
            key={id}
            name="voucher"
            label={voucher.name}
            price={voucher.discount * -1}
            isRemovable={{
              isLoading: isLoading === LOADING_TYPES.voucherRemove(id),
              isDisabled: !!isLoading,
              onRemove: () => onRemoveVoucher(id)
            }}
          />
        ))}
      {showDiscountsPromotion &&
        (promotionDiscounts?.length === 1 ?
          <PriceSection
            name="promotion-savings"
            label={promotionDiscounts[0].name ?? formatMessage({ id: 'payment.promotion.fallbackCodeName' })}
            price={promotionDiscounts[0].discount * -1}
          />
        : <PromotionSection
            total={promotionDiscountTotalAbsolute!}
            promotions={promotionDiscounts}
            isCollapsible={isPromoCollapsible}
          />)}
      {showSystemPromotion && (
        <>
          <PriceSection
            name="promotion-total"
            label={formatMessage({ id: 'basket.total' })}
            price={basketTotal.costWithTaxWithoutPromotion}
          />
          <PriceSection
            name="promotion-savings"
            label={formatMessage({ id: 'basket.yourCouponSavings' })}
            price={discount.promotion * -1}
          />
        </>
      )}
      <PriceSection name="subtotal" label={formatMessage({ id: 'basket.subtotal' })} price={subtotal} />
      {!!pointsRedeemed && (
        <PriceSection
          name="loyalty-program-points"
          label={formatMessage({ id: 'basket.loyaltyProgramPoints' })}
          price={pointsRedeemed * -1}
          isDiscount
        />
      )}
      <PriceSection
        name="shipping-cost"
        label={formatMessage({ id: 'basket.shipping' })}
        price={shippingPrice}
        isFree={shippingPrice === 0}
      />
      {!!infoBoxTexts.length && <InformationBoxAlt texts={infoBoxTexts} />}
      {paymentServices?.map(paymentService => (
        <PriceSection
          key={paymentService.key}
          name={`basket-payment-service-fee-${paymentService.key}`}
          label={formatMessage({ id: `basket.paymentServiceFees.${camelCase(paymentService.key)}` })}
          price={showNetPrices ? paymentService.netValue! : paymentService.value}
        />
      ))}
      {giftCard && (
        <PriceSection
          name="gift-card"
          label={formatMessage({ id: 'basket.giftCard' })}
          price={giftCard.recurringPaymentInformation.remainingAmount * -1}
          isRemovable={{
            isLoading: isLoading === LOADING_TYPES.giftcardRemove(giftCard.recurringPaymentOptionId),
            isDisabled: !!isLoading,
            onRemove: () => onRemoveGiftCard()
          }}
        />
      )}
      {showNetPrices && <PriceSection name="taxes" label={formatMessage({ id: 'basket.taxes' })} price={tax.amount} />}
      <PriceSection
        name="total"
        label={formatMessage({ id: showSystemPromotion ? 'basket.youPay' : 'basket.total' })}
        price={total}
        isLarge
        showIncludesTax
      />
      {isDutiesAndTaxesEnabled && <DutiesAndTaxes pricingEstimations={pricingEstimations} />}
      {showNetPrices && <InformationBoxAlt texts={[formatMessage({ id: 'basket.taxExemptionInfo' })]} />}
      <slot name="slot_basket_under_price_summary" />
    </div>
  );
};

export default PriceSummary;
